import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Badge } from "../components/ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "../components/ui/dialog";
import { Megaphone, Plus, Trash2, Loader2, Eye, EyeOff, AlertTriangle, Info, Wrench, RefreshCw } from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TYPE_OPTIONS = [
  { value: "info", label: "Info", icon: Info, color: "text-blue-400", bg: "bg-blue-500/15 border-blue-500/20" },
  { value: "warning", label: "Warning", icon: AlertTriangle, color: "text-amber-400", bg: "bg-amber-500/15 border-amber-500/20" },
  { value: "maintenance", label: "Maintenance", icon: Wrench, color: "text-violet-400", bg: "bg-violet-500/15 border-violet-500/20" },
];

export default function AnnouncementsPage() {
  const [announcements, setAnnouncements] = useState([]);
  const [loading, setLoading] = useState(true);
  const [createOpen, setCreateOpen] = useState(false);
  const [form, setForm] = useState({ title: "", message: "", type: "info" });
  const [saving, setSaving] = useState(false);

  const fetchData = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/admin/announcements`);
      setAnnouncements(res.data);
    } catch { toast.error("Failed to load announcements"); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleCreate = async () => {
    if (!form.title.trim() || !form.message.trim()) { toast.error("Title and message required"); return; }
    setSaving(true);
    try {
      await axios.post(`${API}/admin/announcements`, form);
      toast.success("Announcement created");
      setCreateOpen(false);
      setForm({ title: "", message: "", type: "info" });
      fetchData();
    } catch { toast.error("Failed to create"); }
    finally { setSaving(false); }
  };

  const handleToggle = async (id) => {
    try {
      const res = await axios.put(`${API}/admin/announcements/${id}/toggle`);
      toast.success(res.data.message);
      fetchData();
    } catch { toast.error("Failed to toggle"); }
  };

  const handleDelete = async (id) => {
    try {
      await axios.delete(`${API}/admin/announcements/${id}`);
      toast.success("Deleted");
      fetchData();
    } catch { toast.error("Failed to delete"); }
  };

  if (loading) return <div className="flex items-center justify-center py-32"><Loader2 className="h-8 w-8 animate-spin text-blue-500" /></div>;

  return (
    <div className="space-y-6" data-testid="announcements-page">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div>
          <h1 className="text-xl sm:text-2xl font-bold text-slate-100" style={{ fontFamily: "Outfit, sans-serif" }}>Announcements</h1>
          <p className="text-xs sm:text-sm text-slate-500 mt-1">Broadcast messages to all users</p>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={() => fetchData()} className="flex items-center gap-1.5 text-slate-400 hover:text-white hover:bg-white/5 rounded-lg px-3 py-1.5 transition-all" data-testid="refresh-announcements">
            <RefreshCw className="h-3.5 w-3.5" /><span className="text-sm">Refresh</span>
          </button>
          <Button size="sm" onClick={() => setCreateOpen(true)} className="bg-blue-600 hover:bg-blue-500" data-testid="create-announcement-btn">
            <Plus className="h-4 w-4 mr-1.5" /> <span className="hidden sm:inline">New </span>Announcement
          </Button>
        </div>
      </div>

      <div className="space-y-3">
        {announcements.length === 0 ? (
          <Card className="bg-slate-900/60 border-slate-800/50">
            <CardContent className="py-16 text-center text-slate-500">No announcements yet</CardContent>
          </Card>
        ) : announcements.map((a) => {
          const typeInfo = TYPE_OPTIONS.find(t => t.value === a.type) || TYPE_OPTIONS[0];
          return (
            <Card key={a.id} className={`border ${a.active ? typeInfo.bg : "bg-slate-900/30 border-slate-800/30 opacity-60"}`}>
              <CardContent className="p-4">
                <div className="flex items-start gap-3">
                  <typeInfo.icon className={`h-5 w-5 ${typeInfo.color} flex-shrink-0 mt-0.5`} />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <h3 className={`text-sm font-semibold ${typeInfo.color}`}>{a.title}</h3>
                      <Badge className={`text-[9px] ${a.active ? "bg-emerald-500/15 text-emerald-400" : "bg-slate-500/15 text-slate-500"}`}>
                        {a.active ? "Active" : "Inactive"}
                      </Badge>
                    </div>
                    <p className="text-xs text-slate-400">{a.message}</p>
                    <p className="text-[10px] text-slate-600 mt-1">{new Date(a.created_at).toLocaleString()}</p>
                  </div>
                  <div className="flex items-center gap-1">
                    <Button variant="ghost" size="icon" onClick={() => handleToggle(a.id)} className="h-7 w-7 text-slate-500 hover:text-white" title={a.active ? "Deactivate" : "Activate"}>
                      {a.active ? <EyeOff className="h-3.5 w-3.5" /> : <Eye className="h-3.5 w-3.5" />}
                    </Button>
                    <Button variant="ghost" size="icon" onClick={() => handleDelete(a.id)} className="h-7 w-7 text-slate-500 hover:text-red-400">
                      <Trash2 className="h-3.5 w-3.5" />
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Create Dialog */}
      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent className="bg-[#11141D] border-white/10 max-w-lg">
          <DialogHeader>
            <DialogTitle className="text-slate-100">New Announcement</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="flex gap-2">
              {TYPE_OPTIONS.map(t => (
                <button key={t.value} onClick={() => setForm({ ...form, type: t.value })} className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs border transition ${form.type === t.value ? t.bg + " " + t.color : "bg-slate-800/50 border-slate-700/50 text-slate-500"}`}>
                  <t.icon className="h-3.5 w-3.5" /> {t.label}
                </button>
              ))}
            </div>
            <Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="Announcement title" className="bg-slate-950/60 border-slate-800/50 text-slate-100" data-testid="announcement-title" />
            <Textarea value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} placeholder="Message to display to all users..." className="bg-slate-950/60 border-slate-800/50 text-slate-200 min-h-[80px]" data-testid="announcement-message" />
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setCreateOpen(false)} className="text-slate-400">Cancel</Button>
            <Button onClick={handleCreate} disabled={saving} className="bg-blue-600 hover:bg-blue-500">
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Publish"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
